import {
  IQuoteComposeEntry,
  IQuoteItemEntry,
  IQuoteSectionEntry,
  isQuoteItemEntry,
  isQuoteSectionEntry,
} from '../models/quote-compose-entry.interface';
import {IQuoteFirestoreItem, IQuoteFirestoreSection} from '../models/quote-firestore.interface';
import {IQuoteStockItem} from '../models/quoteStockItems';

/**
 * Extracts stock items from compose entries in display order.
 */
export const extractQuoteItemsFromComposeEntries = (entries: IQuoteComposeEntry[]): IQuoteStockItem[] => {
  return entries
    .filter(isQuoteItemEntry)
    .map((entry: IQuoteItemEntry) => entry.item)
    .filter((item: IQuoteStockItem) => !!item.code);
};

/**
 * Builds compose entries from legacy item-only drafts.
 */
export const mapQuoteItemsToComposeEntries = (items: IQuoteStockItem[]): IQuoteComposeEntry[] => {
  return items
    .filter((item: IQuoteStockItem) => !!item.code)
    .map((item: IQuoteStockItem): IQuoteItemEntry => ({
      kind: 'item',
      item,
    }));
};

/**
 * Normalises draft compose data from localStorage (supports legacy items-only shape).
 */
export const normaliseQuoteDraftComposeEntries = (
  composeEntries: IQuoteComposeEntry[] | undefined,
  legacyItems: IQuoteStockItem[] | undefined,
): IQuoteComposeEntry[] => {
  if (composeEntries?.length) {
    return composeEntries;
  }

  if (legacyItems?.length) {
    return mapQuoteItemsToComposeEntries(legacyItems);
  }

  return [];
};

/**
 * Builds Firestore sections and ordered items from compose entries.
 */
export const mapComposeEntriesToFirestorePayload = (
  entries: IQuoteComposeEntry[],
): { sections: IQuoteFirestoreSection[]; items: (IQuoteFirestoreItem & { sortOrder: number })[] } => {
  const sections: IQuoteFirestoreSection[] = [];
  let currentSectionId: string | null = null;
  let sortOrder = 0;
  const items: (IQuoteFirestoreItem & { sortOrder: number })[] = [];

  entries.forEach((entry: IQuoteComposeEntry) => {
    if (isQuoteSectionEntry(entry)) {
      currentSectionId = entry.id;
      sections.push({
        id: entry.id,
        name: entry.name,
        sortOrder: sortOrder++,
      });
      return;
    }

    if (!isQuoteItemEntry(entry) || !entry.item.code) {
      return;
    }

    const mapped: IQuoteFirestoreItem & { sortOrder: number } = {
      code: entry.item.code,
      desc: entry.item.desc,
      itemDiscount: Number(entry.item.itemDiscount ?? 0),
      nominalGP: entry.item.nominalGP,
      orderQty: Number(entry.item.orderQty ?? 0),
      originalSellPriIncl1: entry.item.originalSellPriIncl1,
      sectionId: currentSectionId ?? undefined,
      sellPriIncl1: Number(entry.item.sellPriIncl1 ?? 0),
      sortOrder: sortOrder++,
    };
    items.push(mapped);
  });

  return {sections, items};
};

/**
 * Rebuilds compose entries from Firestore sections and saved items.
 */
export const mapFirestoreQuoteToComposeEntries = (
  sections: IQuoteFirestoreSection[] | undefined,
  savedItems: IQuoteFirestoreItem[],
  stockItems: IQuoteStockItem[],
): IQuoteComposeEntry[] => {
  if (!sections?.length) {
    return mapQuoteItemsToComposeEntries(stockItems);
  }

  const stockByCode = new Map<string, IQuoteStockItem>(
    stockItems.map((item: IQuoteStockItem) => [item.code, item]),
  );

  const sortedSections = [...sections].sort(
    (a: IQuoteFirestoreSection, b: IQuoteFirestoreSection) => a.sortOrder - b.sortOrder,
  );

  const sortedItems = [...savedItems].sort(
    (a: IQuoteFirestoreItem, b: IQuoteFirestoreItem) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
  );

  const entries: IQuoteComposeEntry[] = [];
  const itemsBySection = new Map<string | null, IQuoteFirestoreItem[]>();

  sortedItems.forEach((savedItem: IQuoteFirestoreItem) => {
    const key = savedItem.sectionId ?? null;
    const bucket = itemsBySection.get(key) ?? [];
    bucket.push(savedItem);
    itemsBySection.set(key, bucket);
  });

  const unsectioned = itemsBySection.get(null) ?? [];
  unsectioned.forEach((savedItem: IQuoteFirestoreItem) => {
    const stockItem = stockByCode.get(savedItem.code);
    if (stockItem) {
      entries.push({kind: 'item', item: stockItem});
    }
  });

  sortedSections.forEach((section: IQuoteFirestoreSection) => {
    entries.push({
      kind: 'section',
      id: section.id,
      name: section.name,
    });

    const sectionItems = itemsBySection.get(section.id) ?? [];
    sectionItems.forEach((savedItem: IQuoteFirestoreItem) => {
      const stockItem = stockByCode.get(savedItem.code);
      if (stockItem) {
        entries.push({kind: 'item', item: stockItem});
      }
    });
  });

  return entries;
};
